import React, { FormEvent, useState } from "react";
import { Alert, Button, CircularProgress, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import CpfField from "../../atoms/CpfField/CpfField";
import PasswordField from "../../atoms/PasswordField/PasswordField";
import SelectField from "../../atoms/SelectField/SelectField";
import EmailAutocomplete from "../../atoms/EmailAutocomplete/EmailAutoComplete";
import { useCadastro } from "../../../resources/CadastroProvider";
import { isValidCPF } from "../../../utils/DocumentValidators";
import styles from "./CadastroUsuarioForm.module.css";
import styles_button from "../../../styles/Button.module.css";
import { newUser } from "../../../types/Models";

interface Props {
  edit?: boolean;
  isOwnProfileEdit?: boolean;
  isCommonUser?: boolean;
  showUserTypeField?: boolean;
}

const CadastroUsuarioForm = ({
  edit,
  isOwnProfileEdit = false,
  isCommonUser = false,
  showUserTypeField = true,
}: Props) => {
  const {
    userData,
    setStepCompletedAtIndex,
    setUserData,
    handlePrevStep,
    handleNextStep,
    steps,
    handleUpdateUser,
    loadingUpdate,
    loadingValidateExistsUser,
    validadeExistsUser,
  } = useCadastro();

  const [mensagem, setMensagem] = useState("");
  const [newPassword, setNewPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const disableStepActions = loadingValidateExistsUser;
  const disableIdentityFields = edit && isOwnProfileEdit;

  const userTypeMap: { [key: string]: string } = {
    Todos: "",
    "Superadmin": "Superadmin",
    Administrador: "Administrador",
    "Responsável Técnico": "Responsável Técnico",
    "Usuário": "Usuário",
  };

  const menuRoleOptions = Object.keys(userTypeMap)
    .filter((key) => key !== "Todos") // Remove "Todos"
    .map((key) => ({ nome: key }));

  const getTipoUsuarioLabel = (tipoUsuario: string) => {
    for (const key in userTypeMap) {
      if (userTypeMap[key] === tipoUsuario) {
        return key;
      }
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");
    setUserData({ ...userData, nome: sanitizedValue });
  };

  const handleChange = (field: keyof newUser, value: any) => {
    setUserData({ ...userData, [field]: value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setMensagem("");
    setErrors({});

    const indexStep = steps.findIndex(
      (step) => step.label === "Cadastro de Usuário",
    );

    if (indexStep !== -1) {
      setStepCompletedAtIndex(indexStep, false);
    }

    const { nome, cpf, email, confirmarEmail, senha, confirmarSenha, tipo } = userData;

    const validationErrors: Record<string, string> = {};

    if (!nome) {
      validationErrors.nome = "Preencha o nome.";
    }

    if (!cpf) {
      validationErrors.cpf = "Preencha o CPF.";
    } else if (!isValidCPF(cpf)) {
      validationErrors.cpf = "CPF inválido.";
    }

    if (!email) {
      validationErrors.email = "Preencha o e-mail.";
    }

    if (!confirmarEmail) {
      validationErrors.confirmarEmail = "Por favor, confirme o e-mail.";
    }

    if (email && confirmarEmail && email !== confirmarEmail) {
      validationErrors.confirmarEmail = "Os e-mails informados não coincidem.";
    }

    if (!edit || newPassword) {
      if (!senha) {
        validationErrors.senha = "Preencha a senha.";
      } else if (senha.length < 6) {
        validationErrors.senha = "A senha deve ter no mínimo 6 caracteres.";
      }

      if (!confirmarSenha) {
        validationErrors.confirmarSenha = "Por favor, confirme a senha.";
      }

      if (senha && confirmarSenha && senha !== confirmarSenha) {
        validationErrors.confirmarSenha = "As senhas informadas não coincidem.";
      }
    }

    if (edit && showUserTypeField && !tipo) {
      validationErrors.tipo = "Por favor, selecione o tipo de usuário.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Validar existÃªncia do usuário
    const validate = await validadeExistsUser();

    if (validate.exists) {
      setMensagem(validate.message);
      return;
    }

    if (!edit && indexStep !== -1) {
      setStepCompletedAtIndex(indexStep, true);
    }

    if (edit) {

      handleUpdateUser(1);
      cancelNewPassword();
      
    } else {

      handleNextStep();
    }

    setMensagem("");
  };

  const handleEmailChange = (email: string) => {
    setUserData({ ...userData, email });
  };

  /*const handleEmailChange = (email: string, provider: string) => {
    setUserData({ ...userData, email, provedor: provider });
  };*/

  const handleConfirmEmailChange = (confirmarEmail: string) => {
    setUserData({ ...userData, confirmarEmail });
  };

  /*const handleConfirmEmailChange = (email: string, provider: string) => {
    setUserData({
      ...userData,
      confirmarEmail: email,
      confirmarProvedor: provider,
    });
  };*/

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, senha: e.target.value });
  };

  const handleConfirmSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, confirmarSenha: e.target.value });
  };

  const cancelNewPassword = () => {
    setNewPassword(false);
    setUserData({ ...userData, senha: '', confirmarSenha: '' });
  };

  return (
    <div className={styles.container} data-testid="cadastro-usuario-form">
      <div className={styles.card}>
        <div className={styles.title}>
          Cadastro de Usuário
        </div>

        <div className={styles.message}>
          {mensagem && <Typography color="error">{mensagem}</Typography>}
        </div>

        {edit && isOwnProfileEdit && isCommonUser && (
          <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
            Atenção! Não é permitido a alteração de nome e nem CPF, caso seja necessário realizar a alteração em alguma dessas informações, por favor entre em contato através do e-mail dgtep@social.mg.gov.br.
          </Alert>
        )}

        <div className={styles.lineInput}>
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Nome Completo *
            </label>
            <TextField
              size="small"
              placeholder=""
              variant="standard"
              autoFocus={false}
              fullWidth
              data-testid="nome"
              value={userData.nome}
              disabled={disableIdentityFields}
              onChange={handleNameChange}
              required
              error={!!errors.nome}
              helperText={errors.nome}
              inputProps={{ maxLength: 150 }}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                className: styles.input2,
                disableUnderline: true,
              }}
            />
          </div>
            
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              CPF *
            </label>
            <CpfField
              size="small"
              placeholder=""
              variant="standard"
              autoFocus={false}
              value={userData.cpf}
              disabled={disableIdentityFields}
              onUpdate={(value) => setUserData({ ...userData, cpf: value })}
              customClassName={styles.input2}
              error={!!errors.cpf}
              helperText={errors.cpf}
            />
          </div>
        </div>
        
        <div className={styles.inputGroup}>
          <label className={styles.titleInput}>
            E-mail *
          </label>
        <EmailAutocomplete
          label=""
          value={userData.email}
          //onEmailChange={handleEmailChange}
          onChange={handleEmailChange}
          inputProps={{disabledUnderline:true}}
          customClassName={styles.input}
          error={!!errors.email}
          helperText={errors.email}
        />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.titleInput}>
            Confirmar E-mail *
          </label>
        <EmailAutocomplete
          label=""
          value={userData.confirmarEmail}
          onChange={handleConfirmEmailChange}
          inputProps={{disabledUnderline:true}}
          customClassName={styles.input2}
          error={!!errors.confirmarEmail}
          helperText={errors.confirmarEmail}
        />
        </div>

        {!edit || newPassword ? (
          <div className={`${styles.lineInput}`}>
            <div className={styles.inputGroup}>
              <label className={styles.titleInput2}>
                Senha *
              </label>
              <PasswordField
                testeid="senha"
                fullWidth
                label=""
                isLogin={false}
                value={userData.senha}
                onChange={handleSenhaChange}
                margin="normal"
                variant="standard"
                customClassName={styles.input2}
                error={!!errors.senha}
                helperText={errors.senha}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.titleInput2}>
                Confirmar Senha *
              </label>
              <PasswordField
                testeid="confirmarSenha"
                fullWidth
                label=""
                isLogin={false}
                value={userData.confirmarSenha}
                onChange={handleConfirmSenhaChange}
                margin="normal"
                variant="standard"
                customClassName={styles.input2}
                error={!!errors.confirmarSenha}
                helperText={errors.confirmarSenha}
              />
            </div>
          </div>
        ) : ( null )}

        {edit && (
          <>
            {!newPassword && (
              <div className={`${styles.inputGroup} ${styles.marginTop4}`}>
                <Button
                  variant="contained"
                  sx={{
                    width: { xs: '136px', sm: '168px' }, 
                    height: { xs: '42px', sm: '46px' },
                    paddingY: 1, // 8px (MUI spacing unit)
                    paddingX: 6, // 24px (MUI spacing unit)
                    backgroundColor: '#2C3D8F',
                    color: 'white',
                    fontWeight: 500, 
                    textAlign: 'center', 
                    borderRadius: '9999px',
                    '&:hover': {
                      backgroundColor: '#4556A8',
                      boxShadow: 'none', 
                    },
                    transition: 'background-color 200ms ease-in-out', 
                  }}
                  disabled={loadingUpdate} 
                  className={styles_button.buttonSecondary}
                  onClick={() => {setNewPassword(true);}}
                >
                  Alterar Senha
                </Button>
              </div>
            )}
            
            {newPassword && (
              <div className={`${styles.inputGroup} ${styles.marginTop4}`}>
                <Button
                  variant="contained"
                  sx={{
                    width: { xs: '190px', sm: '220px' }, 
                    height: { xs: '42px', sm: '46px' },
                    paddingY: 1, // 8px (MUI spacing unit)
                    paddingX: 6, // 24px (MUI spacing unit)
                    backgroundColor: '#2C3D8F',
                    color: 'white',
                    fontWeight: 500, 
                    textAlign: 'center', 
                    borderRadius: '9999px',
                    '&:hover': {
                      backgroundColor: '#4556A8',
                      boxShadow: 'none', 
                    },
                    transition: 'background-color 200ms ease-in-out', 
                  }}
                  disabled={loadingUpdate} 
                  className={styles_button.buttonDanger}
                  onClick={cancelNewPassword}
                >
                  Cancelar Alteração Senha
                </Button>
              </div>
            )}

          </>
        )}

        {edit && showUserTypeField && (
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Tipo de usuário *
            </label>
          <SelectField
            label=""
            value={getTipoUsuarioLabel(userTypeMap[userData.tipo])}
            onChange={(e) => handleChange("tipo", e)}
            options={menuRoleOptions}
            fullWidth
            error={!!errors.tipo}
            helperText={errors.tipo}
          />
          </div>
        )}

        {edit && showUserTypeField && !isOwnProfileEdit && (
          <div className={styles.inputGroup}>
            <FormControlLabel
              control={
                <Switch
                  checked={userData.force_password_change === true}
                  onChange={(_, checked) => handleChange("force_password_change", checked)}
                />
              }
              label="Forçar mudança de senha"
            />
          </div>
        )}

        {!edit ? (
          <div className={styles.buttonContainer}>
            <Button 
              variant="contained" // Use 'contained' para botÃµes com cor de fundo
              sx={{
                width: { xs: '136px', sm: '168px' }, 
                height: { xs: '42px', sm: '46px' },
                paddingY: 2, // 8px (MUI spacing unit)
                paddingX: 6, // 24px (MUI spacing unit)
                backgroundColor: '#2C3D8F',
                color: 'white',
                fontWeight: 500, 
                textAlign: 'center', 
                borderRadius: '9999px',
                '&:hover': {
                  backgroundColor: '#4556A8',
                  boxShadow: 'none', 
                },
                transition: 'background-color 200ms ease-in-out', 
              }}
              color="primary"
              disabled={disableStepActions}
              onClick={handlePrevStep}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              sx={{
                width: { xs: '136px', sm: '168px' }, 
                height: { xs: '42px', sm: '46px' },
                paddingY: 2, // 8px (MUI spacing unit)
                paddingX: 6, // 24px (MUI spacing unit)
                backgroundColor: '#5A81FA',
                color: 'white',
                fontWeight: 500, 
                textAlign: 'center', 
                borderRadius: '9999px',
                '&:hover': {
                  backgroundColor: '#7A9BFB',
                  boxShadow: 'none', 
                },
                transition: 'background-color 200ms ease-in-out', 
              }}
              disabled={disableStepActions}
              className={styles.nextbutton}
              onClick={handleSubmit}
            >
              {loadingValidateExistsUser ? (
                <>
                  <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
                  Validando...
                </>
              ) : (
                "Próximo"
              )}
            </Button>
          </div>
        ) : (
          <Button
            variant="contained"
            sx={{
              width: { xs: '136px', sm: '168px' }, 
              height: { xs: '42px', sm: '46px' },
              paddingY: 1, // 8px (MUI spacing unit)
              paddingX: 6, // 24px (MUI spacing unit)
              backgroundColor: '#5A81FA',
              color: 'white',
              fontWeight: 500, 
              textAlign: 'center', 
              borderRadius: '9999px',
              '&:hover': {
                backgroundColor: '#7A9BFB',
                boxShadow: 'none', 
              },
              transition: 'background-color 200ms ease-in-out', 
            }}
            disabled={loadingUpdate} 
            className={`${styles_button.buttonPrimary} ${(loadingUpdate) ? styles_button.buttonLoad : ''} `}
            onClick={handleSubmit}
          >
            {loadingUpdate ? (
              <>
              <div className={styles_button.spinner}></div>
              <span>Salvando...</span>
              </>
            ) : (
            <span>Salvar</span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CadastroUsuarioForm;

/*

<EmailField
              valueEmail={userData.email}
              valueProvider={userData.provedor}
              label=""
              titleInput="E-mail"
              dataTestId="email"
              onEmailChange={handleEmailChange}
              required
              customClassName={styles.input2}
            />

<div className="flex justify-between bg-gray-100 p-4 rounded">
  <div className="bg-purple-500 text-white p-2 rounded">Between</div>
  <div className="bg-purple-500 text-white p-2 rounded">Between</div>
</div>

Campos e-mails
<div className={styles.lineInput}>
  <div className={styles.inputGroup}>
    <label className={styles.titleInput}>
      E-mail
    </label>
    <EmailField
      valueEmail={userData.email}
      valueProvider={userData.provedor}
      label="E-mail Pessoal"
      dataTestId="email"
      onEmailChange={handleEmailChange}
      required
      customClassName={styles.input2}
    />
  </div>

  <div className={styles.inputGroup}>
    <label className={styles.titleInput}>
      Confirmar E-mail
    </label>
    <EmailField
      valueEmail={userData.confirmarEmail}
      valueProvider={userData.confirmarProvedor}
      label="Confirmar E-mail"
      dataTestId="confirmaEmail"
      onEmailChange={handleConfirmEmailChange}
      required
      customClassName={styles.input2}
    />
  </div>
</div>
*/

